/** Parser de direcciones para pedidos pegados (Chile, lenguaje coloquial). */

const EXPLICIT_ADDR_PREFIX_RX =
  /^(?:direccion|dirección|domicilio|envio|envío|enviar\s+a|mandar\s+a|llevar\s+a|vivo\s+en|manda\s+a|mándalo\s+a|la\s+direccion\s+es|la\s+dirección\s+es)\s*[:\-]?\s*/i;

const STREET_TYPE_RX =
  /^(?:pasaje|pje|psje|cale|calle|ca|av|avenida|camino|ruta|villa|sector|poblacion|población|los|las|fontt)\.?\s+/i;

const DEPT_TAIL_RX =
  /\s*,?\s*(?:depto|departamento|dep|dpto|block|blk|torre|piso|casa)\.?\s*[a-z0-9-]+$/i;

const PRODUCTISH_LINE_RX =
  /\b(pago|efectivo|transfer|bigmati|smash|burger|hamburguesa|kids|nugget|papas|bebida|lata|pizza|combo|grill)\b/i;

const PAYMENT_TAIL_RX =
  /\s+(?:pago|en\s+(?:efectivo|transferencia|transfer|debito|débito|tarjeta)|con\s+(?:propina|\$?\d)).+$/i;

const COMUNA_TAIL_RX =
  /\s+(?:pago|pagar|efectivo|transfer(?:encia)?|debito|débito|tarjeta|propina|mercadopago|en\s+efect)/i;

const STREET_ARTICLE_RX = /^(?:el|la|los|las)$/i;

const IMPLAUSIBLE_STREET_WORD_RX =
  /^(?:y|en|de|con|para|un|una|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|quiero|dame|pedir|smash|burger|papas|pizza|kids|bebida|lata|schop|roll|gaseosa|combo|nugget|bigmati|classic|grill|hamburguesa|agrego|coca|cola|pepperoni|salmon|atun|sin|fritas|pepinillo|cebolla)$/i;

const STREET_WORD_RX = /[a-záéíóúñ][\wáéíóúñ.-]{2,}/i;

function firstMeaningfulStreetWord(street) {
  for (const part of String(street || '').trim().split(/\s+/)) {
    const w = part.toLowerCase();
    if (!w || STREET_ARTICLE_RX.test(w)) continue;
    return w;
  }
  return '';
}

/** Normaliza abreviaturas de dirección comunes en Chile. */
export function normalizeAddressLine(text) {
  let s = String(text || '')
    .trim()
    .replace(/[\u200b-\u200d\ufeff\u00a0]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\bpje\.?\s*/gi, 'Pasaje ')
    .replace(/\bpsje\.?\s*/gi, 'Pasaje ')
    .replace(/\bav\.?\s*/gi, 'Avenida ')
    .replace(/\bca\.?\s*/gi, 'Calle ')
    .replace(/\s+/g, ' ')
    .trim();
  s = s.replace(EXPLICIT_ADDR_PREFIX_RX, '').trim();
  return s;
}

function trimComunaTail(comuna) {
  let c = String(comuna || '').trim();
  const cut = c.search(COMUNA_TAIL_RX);
  if (cut > 0) c = c.slice(0, cut).trim();
  return c.replace(/[,.;]+$/, '').trim();
}

function isPlausibleShortStreet(street) {
  const word = firstMeaningfulStreetWord(street);
  if (!word || word.length < 3) return false;
  if (IMPLAUSIBLE_STREET_WORD_RX.test(word)) return false;
  if (PRODUCTISH_LINE_RX.test(word)) return false;
  return true;
}

function formatAddressParts(street, comuna, extra = '') {
  const st = String(street || '').replace(/\s+/g, ' ').trim();
  const cm = trimComunaTail(comuna);
  const ex = String(extra || '').replace(/\s+/g, ' ').trim();
  if (!st) return null;
  const parts = [st];
  if (cm) parts.push(cm);
  if (ex) parts.push(ex);
  const full = parts.join(', ').replace(/\s+,/g, ',').trim();
  return full.length >= 5 ? full : null;
}

function streetPrefixRequiredRx() {
  return '(?:pasaje|pje|psje|avenida|av|calle|ca|camino|villa|los|las|fontt)\\.?\\s+';
}

function streetArticlePrefixRx() {
  return '(?:el|la|los|las)\\s+';
}

function namedStreetBodyRx() {
  return `(?:(?:[a-záéíóúñ][\\wáéíóúñ.-]*\\s+)+\\d{1,5}[a-z]?)`;
}

function shortStreetBodyRx() {
  return `(?:${streetArticlePrefixRx()})?(?:${STREET_WORD_RX.source}\\s+\\d{1,5}[a-z]?)`;
}

function articleNamedStreetBodyRx() {
  return `(?:${streetArticlePrefixRx()}${namedStreetBodyRx()})`;
}

function parseStreetAndComuna(s) {
  const dept = s.match(DEPT_TAIL_RX);
  const deptTail = dept ? dept[0].replace(/^,\s*/, '').trim() : '';
  const base = dept ? s.slice(0, dept.index).trim() : s;

  const comunaGroup = '([a-záéíóúñ][\\wáéíóúñ\\s.-]{1,40})';
  const attempts = [
    new RegExp(`^(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s*,\\s*${comunaGroup}$`, 'i'),
    new RegExp(`^(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s+${comunaGroup}$`, 'i'),
    new RegExp(`^(${articleNamedStreetBodyRx()})\\s*,\\s*${comunaGroup}$`, 'i'),
    new RegExp(`^(${articleNamedStreetBodyRx()})\\s+${comunaGroup}$`, 'i'),
    new RegExp(`^(${namedStreetBodyRx()})\\s*,\\s*${comunaGroup}$`, 'i'),
    new RegExp(`^(${namedStreetBodyRx()})\\s+${comunaGroup}$`, 'i'),
    new RegExp(`^(${shortStreetBodyRx()})\\s*,\\s*${comunaGroup}$`, 'i'),
    new RegExp(`^(${shortStreetBodyRx()})\\s+${comunaGroup}$`, 'i'),
  ];

  for (const rx of attempts) {
    const m = base.match(rx);
    if (!m) continue;
    if (
      (rx.source.includes(shortStreetBodyRx()) || rx.source.includes(namedStreetBodyRx())) &&
      !isPlausibleShortStreet(m[1])
    ) {
      continue;
    }
    const full = formatAddressParts(m[1], m[2], deptTail);
    if (full) return full;
  }

  return null;
}

function parseStreetOnly(s) {
  const attempts = [
    new RegExp(`^${streetPrefixRequiredRx()}${namedStreetBodyRx()}$`, 'i'),
    new RegExp(`^${articleNamedStreetBodyRx()}$`, 'i'),
    new RegExp(`^${shortStreetBodyRx()}$`, 'i'),
  ];
  for (const rx of attempts) {
    const m = s.match(rx);
    if (!m) continue;
    if (rx.source.includes(shortStreetBodyRx()) && !isPlausibleShortStreet(m[0])) continue;
    return m[0].trim();
  }
  return null;
}

function matchAddressSuffixAtEnd(raw) {
  const trimmed = raw.replace(PAYMENT_TAIL_RX, '').trim().replace(/[,;.]+\s*$/, '');
  if (!trimmed) return null;

  const comuna =
    '([a-záéíóúñ][\\wáéíóúñ.-]{2,}(?:\\s+[a-záéíóúñ][\\wáéíóúñ.-]{2,}){0,2})';

  const patterns = [
    new RegExp(
      `\\b(?:para|en)\\s+(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s*,\\s*${comuna}\\s*$`,
      'i'
    ),
    new RegExp(`\\b(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s*,\\s*${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(${articleNamedStreetBodyRx()})\\s*,\\s*${comuna}\\s*$`, 'i'),
    new RegExp(
      `\\b(?:para|en)\\s+(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s+${comuna}\\s*$`,
      'i'
    ),
    new RegExp(`\\b(${streetPrefixRequiredRx()}${namedStreetBodyRx()})\\s+${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(?:para|en)\\s+(${articleNamedStreetBodyRx()})\\s+${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(${articleNamedStreetBodyRx()})\\s+${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(?:para|en)\\s+(${shortStreetBodyRx()})\\s*,\\s*${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(${shortStreetBodyRx()})\\s*,\\s*${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(?:para|en)\\s+(${shortStreetBodyRx()})\\s+${comuna}\\s*$`, 'i'),
    new RegExp(`\\b(${shortStreetBodyRx()})\\s+${comuna}\\s*$`, 'i'),
  ];

  for (const rx of patterns) {
    const m = trimmed.match(rx);
    if (!m) continue;
    if (rx.source.includes(shortStreetBodyRx()) && !isPlausibleShortStreet(m[1])) continue;
    const full = formatAddressParts(m[1], m[2]);
    if (!full) continue;
    const index = raw.lastIndexOf(m[0]);
    return { full, index: index >= 0 ? index : m.index ?? 0 };
  }

  return null;
}

/** ¿Línea con patrón calle + número (sin depender de prefijo tipo "calle")? */
export function looksLikeStreetNumberLine(text) {
  const s = normalizeAddressLine(text);
  if (!s || s.length < 5) return false;
  if (/^\d{1,2}\s+[a-záéíóúñ]/i.test(s)) return false;
  return /(?:^|\s)(?:el|la|los|las\s+)?[a-záéíóúñ][\wáéíóúñ.-]*(?:\s+[a-záéíóúñ][\wáéíóúñ.-]*)*\s+\d{1,5}[a-z]?\b/i.test(
    s
  );
}

/**
 * Heurística general: ¿esta línea parece dirección?
 * No consulta la carta — eso se hace en staffTakeOrder para evitar falsos negativos.
 */
export function looksLikeAddressLine(text) {
  const parsed = parseAddressFromLine(text);
  if (parsed?.street) return true;

  const s = normalizeAddressLine(text);
  if (!s || s.length < 5) return false;
  if (/^\d/.test(s) && !STREET_TYPE_RX.test(s)) return false;
  if (PRODUCTISH_LINE_RX.test(s) && !looksLikeStreetNumberLine(s)) return false;

  if (STREET_TYPE_RX.test(s) || looksLikeStreetNumberLine(s)) return true;

  if (/,\s*[a-záéíóúñ]{3,}/i.test(s) && /\d{1,5}[a-z]?\b/.test(s)) {
    return true;
  }

  return false;
}

export function looksLikeStrongComunaLine(text) {
  const s = normalizeAddressLine(text);
  if (!s || /\d/.test(s) || PRODUCTISH_LINE_RX.test(s)) return false;
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length > 4) return false;
  if (words.length >= 2) return true;
  if (words.length === 1) return words[0].length >= 5 && words[0].length <= 22;
  return false;
}

function parseCommaSeparatedAddress(s) {
  const lastComma = String(s || '').lastIndexOf(',');
  if (lastComma <= 0) return null;
  const tail = s.slice(lastComma + 1).trim();
  const head = s.slice(0, lastComma).trim();
  if (!head || !tail) return null;
  if (!looksLikeComunaLine(tail) && tail.length > 24) return null;
  if (!looksLikeStreetNumberLine(head) && !STREET_TYPE_RX.test(head)) return null;
  const full = formatAddressParts(head, tail);
  if (!full) return null;
  return { full, street: head, comuna: tail };
}

/** ¿Línea corta que parece comuna (sin número ni producto)? */
export function looksLikeComunaLine(text) {
  const s = normalizeAddressLine(text);
  if (!s || s.length < 3 || s.length > 48) return false;
  if (/\d/.test(s)) return false;
  if (PRODUCTISH_LINE_RX.test(s)) return false;
  if (/^(region|región|metropolitana|chile|rm|santiago)$/i.test(s)) return true;
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length > 4) return false;
  if (/^(depto|departamento|block|piso|casa|torre)\b/i.test(s)) return false;
  return words.length >= 1;
}

/**
 * Intenta extraer dirección de una línea.
 * @returns {{ full: string, street?: string, comuna?: string } | null}
 */
export function parseAddressFromLine(text) {
  const s = normalizeAddressLine(text);
  if (!s || s.length < 5) return null;
  if (/^\d/.test(s) && !STREET_TYPE_RX.test(s)) return null;
  if (PRODUCTISH_LINE_RX.test(s) && !/\d{1,5}[a-z]?\s*,?\s*[a-záéíóúñ]/i.test(s)) {
    return null;
  }

  const full = parseStreetAndComuna(s);
  if (full) {
    const [street, ...rest] = full.split(',').map((p) => p.trim());
    return { full, street, comuna: rest.join(', ') || undefined };
  }

  const commaParsed = parseCommaSeparatedAddress(s);
  if (commaParsed) return commaParsed;

  const street = parseStreetOnly(s);
  if (street) return { full: street, street, comuna: undefined };

  return null;
}

function indexOfSegment(raw, segment) {
  if (!segment) return -1;
  const idx = raw.lastIndexOf(segment);
  return idx >= 0 ? idx : raw.search(new RegExp(segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'));
}

/** Busca dirección al final de un texto largo (pedido en una sola línea). */
export function findTrailingAddressInText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  const suffixHit = matchAddressSuffixAtEnd(raw);
  if (suffixHit) return suffixHit;

  const candidates = [raw.replace(PAYMENT_TAIL_RX, '').trim(), raw];

  for (const candidate of candidates) {
    const trimmed = candidate.replace(/[,;.]+\s*$/, '').trim();
    if (!trimmed) continue;

    for (const prefix of [/\bpara\s+/i, /\ben\s+/i]) {
      const m = trimmed.match(new RegExp(`${prefix.source}(.+)$`, 'i'));
      if (!m) continue;
      const segment = m[1].trim();
      const parsed = parseAddressFromLine(segment);
      if (parsed?.street && parsed.comuna) {
        return { full: parsed.full, index: indexOfSegment(raw, segment) };
      }
    }

    const parsed = parseAddressFromLine(trimmed);
    if (parsed?.street && parsed.comuna) {
      return { full: parsed.full, index: indexOfSegment(raw, trimmed) };
    }

    const lastComma = trimmed.lastIndexOf(',');
    if (lastComma > 0) {
      const tail = trimmed.slice(lastComma + 1).trim();
      const head = trimmed.slice(0, lastComma).trim();
      if (looksLikeComunaLine(tail) || tail.length <= 24) {
        const merged = parseAddressFromLine(`${head}, ${tail}`);
        if (merged?.street && merged.comuna) {
          return { full: merged.full, index: indexOfSegment(raw, head) };
        }
      }
    }
  }

  return null;
}

/**
 * Busca dirección en cualquier línea del pegado (inicio, medio o final).
 * Tolera productos entre calle y comuna en líneas separadas.
 * @param {string[]} lines
 * @returns {{ address: string, usedIndices: Set<number> }}
 */
export function findAddressInStaffLines(lines) {
  const normalized = (lines || []).map((l) => String(l || '').trim()).filter(Boolean);
  const usedIndices = new Set();

  for (let i = 0; i < normalized.length; i += 1) {
    const parsed = parseAddressFromLine(normalized[i]);
    if (parsed?.street && parsed.comuna) {
      return { address: parsed.full, usedIndices: new Set([i]) };
    }
  }

  for (let i = 0; i < normalized.length; i += 1) {
    const parsed = parseAddressFromLine(normalized[i]);
    const streetCandidate =
      parsed?.street ||
      (looksLikeStreetNumberLine(normalized[i]) ? normalizeAddressLine(normalized[i]) : null);
    if (!streetCandidate) continue;

    for (let j = i + 1; j < Math.min(i + 6, normalized.length); j += 1) {
      const comunaOk =
        j === i + 1 ? looksLikeComunaLine(normalized[j]) : looksLikeStrongComunaLine(normalized[j]);
      if (!comunaOk) continue;
      const full = formatAddressParts(streetCandidate, normalized[j]);
      if (!full) continue;
      return { address: full, usedIndices: new Set([i, j]) };
    }
  }

  for (let i = 0; i < normalized.length - 1; i += 1) {
    const parsed = parseAddressFromLine(normalized[i]);
    if (parsed?.street && !parsed.comuna && looksLikeComunaLine(normalized[i + 1])) {
      const full = formatAddressParts(parsed.street, normalized[i + 1]);
      if (full) return { address: full, usedIndices: new Set([i, i + 1]) };
    }
  }

  return { address: '', usedIndices };
}

/**
 * Procesa líneas multilínea: une calle + comuna en líneas consecutivas.
 * @param {string[]} lines
 * @returns {{ address: string, otherLines: string[] }}
 */
export function extractAddressFromLines(lines) {
  const otherLines = [];
  let address = '';
  let pendingStreet = '';

  for (const rawLine of lines) {
    const line = String(rawLine || '').trim();
    if (!line) continue;

    const parsed = parseAddressFromLine(line);
    if (parsed?.street && parsed.comuna) {
      address = parsed.full;
      pendingStreet = '';
      continue;
    }
    if (parsed?.street && !parsed.comuna) {
      pendingStreet = parsed.street;
      continue;
    }
    if (looksLikeStreetNumberLine(line)) {
      const commaParsed = parseCommaSeparatedAddress(normalizeAddressLine(line));
      if (commaParsed?.full) {
        address = commaParsed.full;
        pendingStreet = '';
        continue;
      }
      pendingStreet = line;
      continue;
    }
    if (pendingStreet && looksLikeComunaLine(line)) {
      address = formatAddressParts(pendingStreet, line) || '';
      pendingStreet = '';
      continue;
    }
    if (pendingStreet) {
      otherLines.push(line);
      continue;
    }
    otherLines.push(line);
  }

  if (pendingStreet && !address) {
    const parsed = parseAddressFromLine(pendingStreet);
    if (parsed?.full) {
      address = parsed.full;
    } else {
      otherLines.push(pendingStreet);
    }
  }

  return { address, otherLines };
}
